import React, { createContext, useContext, useRef, ReactNode } from "react";
import { Toast } from "primereact/toast";

interface ToastMessage {
  severity: "success" | "info" | "warn" | "error";
  summary: string;
  detail?: string;
  life?: number;
}

interface ToastContextType {
  showToast: (message: ToastMessage) => void;
  showSuccess: (summary: string, detail?: string) => void;
  showError: (summary: string, detail?: string) => void;
  showInfo: (summary: string, detail?: string) => void;
  showWarn: (summary: string, detail?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const toastRef = useRef<Toast>(null);

  const showToast = (message: ToastMessage) => {
    toastRef.current?.show({
      severity: message.severity,
      summary: message.summary,
      detail: message.detail,
      life: message.life || 3000,
    });
  };

  const showSuccess = (summary: string, detail?: string) => {
    showToast({ severity: "success", summary, detail });
  };

  const showError = (summary: string, detail?: string) => {
    showToast({ severity: "error", summary, detail });
  };

  const showInfo = (summary: string, detail?: string) => {
    showToast({ severity: "info", summary, detail });
  };

  const showWarn = (summary: string, detail?: string) => {
    showToast({ severity: "warn", summary, detail });
  };

  const value: ToastContextType = {
    showToast,
    showSuccess,
    showError,
    showInfo,
    showWarn,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toast
        ref={toastRef}
        position="bottom-center"
        className="global-toast"
        style={{ zIndex: 9999 }}
      />
    </ToastContext.Provider>
  );
};
